/**
 * "연관 biz 바로가기" 딥링크 매핑 (queue #612 · docs/biz-specs/common-showcase.md §R612-1).
 *
 * 이미 owner biz 에 전용 인터랙티브 콘솔이 시연 완료된 능력(C036 세션 · C061 TCP · C062 gRPC ·
 * C074 SMS · C075 Push)은 쇼케이스 내부에 데모 패널을 신규로 만들지 않고, 노드의 `code` 기준으로
 * owner biz 의 데모 화면 라우트로 딥링크만 제공한다 (§R612-1 근거 1~4 — 권한 모델 불일치 · FE 중복
 * 방지 · 신규 데모 BE/패널 0 원칙 · cross-feature demoEndpoint 무의미).
 *
 * `usedByBiz` 만으로는 TCP/gRPC 처럼 같은 biz 의 서로 다른 서브라우트를 구분할 수 없으므로
 * 노드 `code` 기준 소형 lookup 으로 구현한다 (§R612-1 표 하단 설명).
 *
 * #617 — P2~P5 신규 13 biz 가 1차/부가 시연하는 18코드 추가. 매핑은 `docs/biz-specs/_capability-map.md`
 * (1차/부가 시연 표) + `apps/web/src/router.tsx` 의 실제 라우트로 교차 확인했다. 각 biz 는 단일
 * 콘솔 라우트(서브라우트 없음)이므로 같은 biz 의 여러 코드는 동일 route 를 공유한다 — 단,
 * integration-hub 만 code 별 서브라우트(tcp/grpc)가 이미 있어 그 패턴을 유지한다.
 *
 * C022(분산락)는 saga-monitor(보조: resume 직렬화)와 scheduler-monitor(보조: 트리거 중복 방지)
 * 양쪽에서 재사용되지만, `router.tsx` 의 scheduler-monitor 라우트 주석(#556)이 C022 를 명시
 * 소유로 표기하고 동시 트리거 부하 시나리오(S4)로 실제 라이브 시연이 있어 scheduler-monitor 로 단일
 * 매핑한다. C021(멱등성)은 saga-monitor 전용 데모 트리거에서만 시연되어 충돌 없음.
 *
 * #646: 10 신규 demoEndpoint 노드(C066/C067/C082/C027/C085/C081/C011/C079/C080/C020)는
 * 이 표에 전무(grep 0건) — 딥링크와 신규 쇼케이스 데모가 상호배타임을 확인했다(항목 변경 없음).
 */
export interface RelatedDemoRoute {
  /** owner biz 의 데모 화면 라우트 (react-router path) */
  route: string;
  /** i18n 키 (showcase.related.* 네임스페이스) */
  i18nKey: string;
}

export const RELATED_DEMO_ROUTE: Record<string, RelatedDemoRoute> = {
  C036: { route: '/platform-lab', i18nKey: 'showcase.related.platformLabSession' },
  C061: { route: '/integration-hub/tcp', i18nKey: 'showcase.related.integrationTcp' },
  C062: { route: '/integration-hub/grpc', i18nKey: 'showcase.related.integrationGrpc' },
  C074: { route: '/notification-center', i18nKey: 'showcase.related.notificationSms' },
  C075: { route: '/notification-center', i18nKey: 'showcase.related.notificationPush' },
  C068: { route: '/approval-center', i18nKey: 'showcase.related.workflowApproval' },

  // #617 — platform-lab (§561 C025/C019/C023/C018 core 잔여 primitive 묶음)
  C018: { route: '/platform-lab', i18nKey: 'showcase.related.platformLabResilience' },
  C019: { route: '/platform-lab', i18nKey: 'showcase.related.platformLabAsync' },
  C023: { route: '/platform-lab', i18nKey: 'showcase.related.platformLabSequence' },
  C025: { route: '/platform-lab', i18nKey: 'showcase.related.platformLabApiVersion' },

  // #617 — saga-monitor (§549 C068 1차 + C021 보조)
  C021: { route: '/saga-monitor', i18nKey: 'showcase.related.sagaMonitorIdempotency' },

  // #617 — tenant-switcher (§553 C046/C049 1차 + C044 부가)
  C044: { route: '/tenant-switcher', i18nKey: 'showcase.related.tenantSwitcherSensitive' },
  C046: { route: '/tenant-switcher', i18nKey: 'showcase.related.tenantSwitcherMultiTenancy' },

  // #617 — change-history-viewer (§558 C047/C048 1차 + C045 부가)
  C047: { route: '/change-history-viewer', i18nKey: 'showcase.related.changeHistoryEntity' },
  C048: { route: '/change-history-viewer', i18nKey: 'showcase.related.changeHistoryCursor' },

  // #617 — cache-admin (§559 C051/C050 1차 + C026 부가)
  C050: { route: '/cache-admin', i18nKey: 'showcase.related.cacheAdminRedis' },
  C051: { route: '/cache-admin', i18nKey: 'showcase.related.cacheAdminL1' },

  // #617 — integration-hub (§555 C058 1차 — main 콘솔에서 Feign 데모, 서브라우트 없음)
  C058: { route: '/integration-hub', i18nKey: 'showcase.related.integrationFeign' },

  // #617 — scheduler-monitor (§556 C064/C065 1차 + C022 보조, 위 comment 참고)
  C022: { route: '/scheduler-monitor', i18nKey: 'showcase.related.schedulerMonitorLock' },
  C064: { route: '/scheduler-monitor', i18nKey: 'showcase.related.schedulerMonitorSchedule' },
  C065: { route: '/scheduler-monitor', i18nKey: 'showcase.related.schedulerMonitorBatch' },

  // #617 — notification-center (§550 C077/C076 1차 + C073 부가, C074/C075 는 위에 기존 매핑)
  C073: { route: '/notification-center', i18nKey: 'showcase.related.notificationEmail' },
  C076: { route: '/notification-center', i18nKey: 'showcase.related.notificationWebhook' },
  C077: { route: '/notification-center', i18nKey: 'showcase.related.notificationHistory' },
};
